import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import Checkbox from "../../../../../../components/Checkbox";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { IOperacoesPatioFaturamento } from "../../types/types";
import formValidator from "../validators/formValidator";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface FormValues {
  id_transportadora: string;
  placa: string;
  faturamento_temporario: boolean;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IOperacoesPatioFaturamento;
  onConfirm: () => void;
  onClose: () => void;
}

interface Option {
  value: number | string;
  label: string;
}

const Form: React.FC<Props> = (props: Props) => {
  const [transportadoras, setTransportadoras] = useState<Option[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();

  const handleSubmit = useCallback(
    async (values: FormValues, row?: IOperacoesPatioFaturamento) => {
      try {
        setLoading(true);

        setLoading(true);

        const body = {
          id_transportadora_veiculo: row?.id_transportadora_veiculo,
          id_transportadora: values.id_transportadora,
          placa: values.placa,
          faturamento_temporario: values.faturamento_temporario,
          id_usuario_historico: user?.id,
          ativo: true,
        };

        if (props.isEdit) {
          await api.post("/editar/transportadoraVeiculos", body);
        } else {
          await api.post("/cadastro/transportadoraVeiculo", body);
        }

        setLoading(false);
        props.onConfirm();
      } catch {
        setLoading(false);
      }
    },
    [props.isEdit, props.onConfirm, user]
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      id_transportadora: "",
      placa: "",
      faturamento_temporario: false,
    },
    validationSchema: formValidator,
    onSubmit: (values) => {
      handleSubmit(values, props.selectedRow);
    },
  });

  const onLoadFormValues = useCallback(
    (row: IOperacoesPatioFaturamento) => {
      if (row) {
        formik.setFieldValue("id_transportadora", row.id_transportadora);
        formik.setFieldValue("placa", row.placa_dianteira_veiculo);
        formik.setFieldValue("faturamento_temporario", row.faturamento_temporario);
      }
    },
    [formik]
  );

  const getTransportadoras = useCallback(async () => {
    try {
      setLoading(true);
      const body = {
        qtd_por_pagina: 100,
        order_by: "razao_social",
        order_direction: "asc",
      };
      const response = await api.post("/listar/transportadoras", body);
      const mappingData = response.data.data.map((rows: { id_transportadora: number; razao_social: string }) => {
        return {
          value: rows.id_transportadora,
          label: rows.razao_social,
        };
      });
      setTransportadoras(mappingData);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getTransportadoras();
  }, [getTransportadoras]);

  useEffect(() => {
    if (props.selectedRow) {
      onLoadFormValues(props.selectedRow);
    }
  }, [props.selectedRow, onLoadFormValues]);

  return (
    <>
      <Loading loading={loading} skeletonFields={3} skeletonColumns={1} />
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-1 gap-4">
          <SelectCustom
            title="Transportadora"
            data={transportadoras}
            value={formik.values.id_transportadora}
            onChange={(selectedOption: Option) =>
              formik.setFieldValue("id_transportadora", selectedOption?.value)
            }
            error={formik.errors.id_transportadora}
            touched={formik.touched.id_transportadora}
            disabled={props.isView}
          />
          <InputCustom
            title="Placa"
            name="placa"
            placeholder="Digite a placa"
            onChange={formik.handleChange}
            value={formik.values.placa}
            error={formik.errors.placa}
            touched={formik.touched.placa}
            disabled={props.isView}
          />
          <div className="flex items-center mt-4">
            <Checkbox
              title="Faturamento Temporário"
              checked={formik.values.faturamento_temporario}
              onChecked={() =>
                formik.setFieldValue("faturamento_temporario", !formik.values.faturamento_temporario)
              }
            />
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            type="button"
            className="mr-2 px-4 py-2 bg-gray-300 rounded"
            onClick={props.onClose}
          >
            Cancelar
          </button>
          {!props.isView && (
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Salvar
            </button>
          )}
        </div>
      </form>
    </>
  );
};

export default Form;
